/** @file useTileKeys: pushes the `tile` key context and registers `tileBindings` while a Tile tab shows. */
import { applyOverrides } from '@zilo/pose3d-shortcuts/applyOverrides';
import { pushContext } from '@zilo/pose3d-shortcuts/pushContext';
import { registerBindings } from '@zilo/pose3d-shortcuts/registerBindings';
import { useEffect, useRef } from 'react';
import { EDITOR_SETTINGS } from '../../shell/editorSettings';
import { PLATFORM } from '../../shell/platform';
import { tileBindings } from './tileBindings';

/**
 * Registers once with the user's overrides (Settings) applied; each key calls the latest `handlers[id]` (handlers may
 * change every render). The `tile` context sits above `global`, so Mod+Z here is the tab's undo, not the server's.
 */
export function useTileKeys(handlers: Record<string, () => void>): void {
  const latest = useRef(handlers);
  latest.current = handlers;
  useEffect(() => {
    const { table } = applyOverrides(tileBindings, EDITOR_SETTINGS.shortcuts.load(), PLATFORM);
    const stable = Object.fromEntries(table.map((b) => [b.id, () => { latest.current[b.id]?.(); }]));
    const pop = pushContext('tile');
    const unregister = registerBindings('tile', table, stable);
    return () => { unregister(); pop(); };
  }, []);
}
